import { useTranslation } from 'next-i18next';
import { Input, Button } from '@components/common/Forms';
import { Anchor } from '@components/common';
import { usePhoneNumberFlow } from '@components/features/modals/phone/hooks/usePhoneNumberFlow';
import { PhoneNumberGroup } from '@components/features/modals/phone/partials/PhoneNumberGroup';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

/** Props */
interface PhoneFlowHubProps {
  locale: Locale;
  startFromConfirm?: boolean;
  successSavePhoneCallback?: (response?: ApiSchema<'PhoneNumberInfo'>) => void;
  successConfirmPhoneCallback?: () => void;
}

/**
 * @description Phone flow hub component. Handles the phone asking and confirmation steps.
 * - `locale` - The locale.
 * - `startFromConfirm` - Start from the phone confirm step.
 * - `successSavePhoneCallback` - The success callback for saving the phone number.
 * - `successConfirmPhoneCallback` - The success callback for confirming the phone number.
 */
export const PhoneFlowHub = (props: PhoneFlowHubProps) => {
  // Destructure props
  const {
    locale,
    startFromConfirm = false,
    successSavePhoneCallback,
    successConfirmPhoneCallback,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Phone number flow hook
  // ************************
  const {
    phoneNumber,
    setPhoneNumber,
    phonePrefixes,
    selectedCountryCode,
    setSelectedCountryCode,
    phoneValidityStates,
    setPhoneValidityStates,
    verifyUserPhoneInfo,
    sendSMSToUser,
    isUserPhoneInfoMutating,
    isSendingSms,
    numberAlreadyInUse,
    showConfirmStep,
    confirmSmsCode,
    setConfirmSmsCode,
    confirmUserPhone,
    isUserPhoneConfirmMutating,
    smsCodeValidityStates,
    setSmsCodeValidityStates,
  } = usePhoneNumberFlow({
    locale,
    startFromConfirm,
    successSavePhoneCallback,
    successConfirmPhoneCallback,
  });

  // Render component
  // **********************
  return (
    <>
      {/* Phone number group */}
      {!showConfirmStep && (
        <>
          {/* Phone input group */}
          <div className="bg-surface-100 p-6">
            <PhoneNumberGroup
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              phonePrefixes={phonePrefixes}
              selectedCountryCode={selectedCountryCode}
              setSelectedCountryCode={setSelectedCountryCode}
              phoneValidityStates={phoneValidityStates}
              setPhoneValidityStates={setPhoneValidityStates}
            />

            {/* Number already in use */}
            {numberAlreadyInUse && (
              <div className="mt-4 flex items-start gap-1.5 rounded bg-warning/10 p-2 text-sm">
                <ExclamationTriangleIcon className="size-5 shrink-0 text-warning" />
                <div>
                  {`${t('phone.claim.duplicate.number2')} `}
                  <Anchor
                    href="user_logout_reset"
                    size="sm"
                    styling="text"
                    symfonyRoute
                  >
                    {t('phone-claim-option-recover')}
                  </Anchor>
                </div>
              </div>
            )}
          </div>

          {/* Save button */}
          <div className="border-t bg-surface px-6 py-4 text-right">
            <Button
              type="button"
              color="secondary"
              size="sm"
              rounding="full"
              className="w-full sm:w-auto sm:!px-7"
              disabled={phoneNumber.length === 0 || phoneValidityStates.isInvalid}
              isLoading={isUserPhoneInfoMutating || isSendingSms}
              onClick={verifyUserPhoneInfo}
            >
              {t('global.action.save')}
            </Button>
          </div>
        </>
      )}

      {/* Phone confirm group */}
      {showConfirmStep && (
        <>
          {/* Confirm phone input group */}
          <div className="bg-surface-100 p-6">
            <p>{t('account.confirm.phone.code.info')}</p>
            <div className="mt-4">
              <Input
                required
                type="tel"
                minLength={4}
                maxLength={4}
                value={confirmSmsCode}
                pattern="^\d{4}$"
                patternMismatchMessage={t('sms.confirm.input.pattern')}
                getValidityStates={setSmsCodeValidityStates}
                onChange={(e) => {
                  setConfirmSmsCode(e.target.value);
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-4 border-t bg-surface px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Resend code */}
            <div className="text-center text-sm sm:text-left">
              {t('account.settings.label.no_sms_received')}
              <Button
                type="button"
                styling="text"
                size="sm"
                rounding="full"
                className="ml-1"
                onClick={sendSMSToUser}
              >
                {t('account.settings.label.resend_sms')}
              </Button>
            </div>

            {/* Confirm button */}
            <Button
              type="button"
              color="secondary"
              size="sm"
              rounding="full"
              className="w-full sm:w-auto sm:!px-7"
              disabled={smsCodeValidityStates.isInvalid || confirmSmsCode.length !== 4}
              isLoading={isUserPhoneConfirmMutating}
              onClick={confirmUserPhone}
            >
              {t('global.action.confirm')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};
